import { createFileRoute } from '@tanstack/react-router'
import { useParams } from '@tanstack/react-router'
import { useEstablishments } from '@/hooks/useEstablishments'
import EstablishmentDirectUpdate from '@/components/card/EstablishmentDirectUpdate'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { ArrowLeft } from 'lucide-react'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { guardSuperAdminOnly } from '@/lib/auth/routeGuards'

export const Route = createFileRoute(
  '/super_admin/direct-update-establishment/$id'
)({
  beforeLoad: guardSuperAdminOnly,
  component: DirectUpdateEstablishmentPage,
})

function DirectUpdateEstablishmentPage() {
  // Translation hook
  const { t } = useTranslation('superAdmin')

  const { id } = useParams({
    from: '/super_admin/direct-update-establishment/$id',
  })
  const { getEstablishmentQuery } = useEstablishments()
  const { data: establishment, isLoading, isError } = getEstablishmentQuery(id)

  if (isLoading) {
    return <div className="p-4">{t('directUpdateEstablishment.loading')}</div>
  }

  if (isError || !establishment) {
    return <div className="p-4">{t('directUpdateEstablishment.error')}</div>
  }

  return (
    <div className="container mx-auto p-4">
      <div className="mb-4">
        <Link to="/super_admin/etablisments">
          <Button variant="outline" size="sm">
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('directUpdateEstablishment.backButton')}
          </Button>
        </Link>
      </div>

      <div className="grid grid-cols-1 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>{t('directUpdateEstablishment.header.title')}</CardTitle>
            <CardDescription>
              {t('directUpdateEstablishment.header.description', { establishmentName: establishment.name })}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div>
                <strong>{t('directUpdateEstablishment.info.id')}:</strong> {establishment.id}
              </div>
              <div>
                <strong>{t('directUpdateEstablishment.info.name')}:</strong> {establishment.name}
              </div>
              <div>
                <strong>{t('directUpdateEstablishment.info.address')}:</strong> {establishment.address}
              </div>
            </div>
          </CardContent>
        </Card>

        <EstablishmentDirectUpdate establishmentId={id} />

        <Card>
          <CardHeader>
            <CardTitle>{t('directUpdateEstablishment.exampleCard.title')}</CardTitle>
            <CardDescription>
              {t('directUpdateEstablishment.exampleCard.description')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <pre className="bg-gray-100 p-4 rounded-md overflow-auto text-xs">
              {JSON.stringify(
                {
                  name: 'Example School Name',
                  address: '123 Education Avenue, Jerid, Tunisia',
                  logo: 'https://example.com/logo.png',
                  url: 'example-school',
                  CMSContent:
                    '<h1>Welcome to Our School</h1><p>A premier educational institution dedicated to excellence.</p>',
                  description:
                    'Example School is a leading educational institution offering comprehensive programs.',
                  heroImage: 'https://example.com/hero-image.jpg',
                  galleryImages: [
                    'https://example.com/gallery1.jpg',
                    'https://example.com/gallery2.jpg',
                  ],
                  contactEmail: 'info@example.edu',
                  contactPhone: '+21612345678',
                  socialLinks: {
                    facebook: 'https://facebook.com/example',
                    twitter: 'https://twitter.com/example',
                    instagram: 'https://instagram.com/example',
                    linkedin: 'https://linkedin.com/company/example',
                  },
                  stats: {
                    students: 850,
                    teachers: 65,
                    courses: 42,
                    awards: 28,
                  },
                  services: [
                    'Academic Excellence',
                    'Sports Programs',
                    'Arts & Music',
                  ],
                  isActive: true,
                },
                null,
                2
              )}
            </pre>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

export default DirectUpdateEstablishmentPage
